import { useConnectModal } from '@rainbow-me/rainbowkit';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { UserRejectedRequestError } from 'viem';
import { useAccount, useSwitchChain } from 'wagmi';
import { type ChainId, parseEvmChainId, isSolanaChain, isTronChain } from '@/shared/assets/chains';
import Button, { type ButtonProps } from '@/shared/components/flip-ui-kit/Button';
import { Tooltip } from '@/shared/components/molecules/Tooltip';
import { useTronWallet } from '@/shared/components/TronConnect';
import useToast from '@/shared/hooks/useToast';
import { noop } from '@/shared/utils/helpers';

type Props = {
  requireConnection: boolean;
  chainId: ChainId | undefined;
  tooltip?: string;
  connectWalletContent?: React.ReactNode;
  switchNetworkContent?: React.ReactNode;
} & ButtonProps;

const EvmButton = ({
  chainId,
  requireConnection,
  tooltip,
  connectWalletContent,
  switchNetworkContent,
  ...props
}: Props) => {
  const { isConnected, chain } = useAccount();
  const { switchChain } = useSwitchChain();
  const { openConnectModal } = useConnectModal();
  const toast = useToast('switch-network');

  const srcTokenChainId = parseEvmChainId(chainId) || 1;

  let { children, onClick } = props;
  if (requireConnection && !isConnected) {
    children = connectWalletContent ?? 'Connect Wallet';
    onClick = openConnectModal;
  } else if (requireConnection && srcTokenChainId !== chain?.id) {
    children = switchNetworkContent ?? 'Switch Network';
    onClick = () =>
      switchChain(
        { chainId: srcTokenChainId },
        {
          onError: (error) => {
            if (error instanceof UserRejectedRequestError) return;
            toast.error('Failed to switch network', {
              description: 'shortMessage' in error ? error.shortMessage : error.message,
            });
          },
        },
      );
  }

  return (
    <Tooltip content={tooltip} disabled={!tooltip} fullWidth={props.fullWidth}>
      <Button {...props} onClick={onClick}>
        {children}
      </Button>
    </Tooltip>
  );
};

const SolanaButton = ({
  chainId,
  requireConnection,
  tooltip,
  connectWalletContent,
  ...props
}: Props) => {
  const { signTransaction } = useWallet();
  const { setVisible } = useWalletModal();

  let { children, onClick } = props;
  if (requireConnection && !signTransaction) {
    children = connectWalletContent ?? 'Connect Wallet';
    onClick = () => {
      setVisible(true);
    };
  }

  return (
    <Tooltip content={tooltip} disabled={!tooltip}>
      <Button {...props} onClick={onClick}>
        {children}
      </Button>
    </Tooltip>
  );
};

const TronButton = ({
  requireConnection,
  tooltip,
  connectWalletContent,
  ...props
}: Omit<Props, 'chainId'>) => {
  const tronWallet = useTronWallet();

  let { children, onClick } = props;
  if (requireConnection && !tronWallet.isConnected) {
    children = connectWalletContent ?? 'Connect TronLink';
    onClick = () => {
      tronWallet.connect().catch(noop);
    };
  }

  return (
    <Tooltip content={tooltip} disabled={!tooltip}>
      <Button {...props} onClick={onClick}>
        {children}
      </Button>
    </Tooltip>
  );
};

const WalletActionButton = ({ chainId, ...props }: Props): React.JSX.Element | null => {
  if (isSolanaChain(chainId)) return <SolanaButton chainId={chainId} {...props} />;
  if (isTronChain(chainId)) return <TronButton {...props} />;
  return <EvmButton chainId={chainId} {...props} />;
};

export default WalletActionButton;
